import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles, Sword, Trophy, Award, BarChart3, ArrowRight, ArrowDownCircle, LogOut, Moon, Settings, Trash2, ChevronDown, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getMyProfile, getMyMessages, streamAction, levelUp, deleteAccount } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CharacterSheet from "@/components/CharacterSheet";
import StoryLog from "@/components/StoryLog";
import DiceRollOverlay from "@/components/DiceRollOverlay";
import LevelUpDialog from "@/components/LevelUpDialog";
import ActionButtons, { PRESET_ACTIONS } from "@/components/QuickActions";
import ShareCardDialog from "@/components/ShareCardDialog";
import WeeklyChallenge from "@/components/WeeklyChallenge";

export default function GamePage() {
  const navigate = useNavigate();
  const { user, setUser, logout, refresh } = useAuth();

  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingGmText, setStreamingGmText] = useState("");
  const [diceOverlay, setDiceOverlay] = useState(null);
  const [levelUpOpen, setLevelUpOpen] = useState(false);
  const [levelUpAvailable, setLevelUpAvailable] = useState(false);
  const [shareMessage, setShareMessage] = useState(null);
  const [challengeKey, setChallengeKey] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [scrollTargetId, setScrollTargetId] = useState(null);
  const [showLiveBadge, setShowLiveBadge] = useState(false);

  const logRef = useRef(null);
  const streamingBubbleRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([getMyProfile(), getMyMessages()]);
        setProfile(p);
        setMessages(m);
        if (p.xp >= p.level * (p.level + 1) * 50) setLevelUpAvailable(true);
        // Geburtstags-Geschenk (max 1× pro Tag, Backend liefert nur am Stichtag)
        if (p.birthday_gift && p.birthday_gift.name) {
          const seenKey = `birthday_seen_${p.birthday_gift.date || ""}`;
          if (!sessionStorage.getItem(seenKey)) {
            toast.success(`🎂 ${p.birthday_gift.name} hat heute Geburtstag!`, {
              description: `Hammerl spendiert dir ${p.birthday_gift.xp_granted} EXP als Geschenk zum Ehrentag.`,
              duration: 8000,
            });
            sessionStorage.setItem(seenKey, "1");
          }
        }
        // Initial: jump to bottom so user sees latest content
        requestAnimationFrame(() => {
          if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
        });
      } catch (e) {
        console.error("[GamePage] Initial profile/messages load failed:", e);
        toast.error("Profil konnte nicht geladen werden.");
      }
    })();
  }, []);

  // Smoothly scroll to a specific message's TOP when its id becomes the target
  useEffect(() => {
    if (!scrollTargetId) return;
    const el = document.querySelector(`[data-message-id="${scrollTargetId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setScrollTargetId(null);
  }, [scrollTargetId, messages]);

  // Show floating "GM tippt..." badge if streaming and the live bubble is off-screen
  useEffect(() => {
    if (!sending) {
      setShowLiveBadge(false);
      return;
    }
    const container = logRef.current;
    const onScroll = () => {
      const bubble = streamingBubbleRef.current;
      if (!container || !bubble) {
        setShowLiveBadge(true);
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const bubbleRect = bubble.getBoundingClientRect();
      // Visible if any part is in viewport of container
      const visible = bubbleRect.top < containerRect.bottom && bubbleRect.bottom > containerRect.top;
      setShowLiveBadge(!visible);
    };
    onScroll();
    container?.addEventListener("scroll", onScroll, { passive: true });
    return () => container?.removeEventListener("scroll", onScroll);
  }, [sending, streamingGmText]);

  // Daily check: also re-fetch profile when tab becomes visible again, or every 30 min.
  // Trifft den Fall ab, dass der User auto-eingeloggt bleibt und den Browser nicht neu öffnet —
  // z.B. um Mitternacht in den Geburtstags-Tag rollt.
  useEffect(() => {
    let lastFetchDay = new Date().toISOString().slice(0, 10);
    const checkBirthday = (p) => {
      if (!p?.birthday_gift?.name) return;
      const seenKey = `birthday_seen_${p.birthday_gift.date || ""}`;
      if (sessionStorage.getItem(seenKey)) return;
      toast.success(`🎂 ${p.birthday_gift.name} hat heute Geburtstag!`, {
        description: `Hammerl spendiert dir ${p.birthday_gift.xp_granted} EXP als Geschenk zum Ehrentag.`,
        duration: 8000,
      });
      sessionStorage.setItem(seenKey, "1");
    };
    const refresh = async () => {
      try {
        const p = await getMyProfile();
        setProfile(p);
        checkBirthday(p);
        lastFetchDay = new Date().toISOString().slice(0, 10);
      } catch (e) {
        console.warn("[GamePage] background profile refresh failed:", e);
      }
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Re-fetch wenn ein neuer Kalendertag begonnen hat ODER >5min nicht aktualisiert
      const today = new Date().toISOString().slice(0, 10);
      if (today !== lastFetchDay) refresh();
    };
    const interval = setInterval(refresh, 30 * 60 * 1000); // alle 30min
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Scroll-to-Bottom Pfeil: sichtbar wenn nicht am unteren Ende.
  // Auf Desktop scrollt der logRef-Container, auf Mobile scrollt das window (da main
  // dort keine max-h hat). Wir hören auf BEIDES und berechnen die Distanz vom richtigen Target.
  const [showScrollDown, setShowScrollDown] = useState(false);
  useEffect(() => {
    const container = logRef.current;
    const isContainerScrollable = () =>
      !!container && container.scrollHeight - container.clientHeight > 20;
    const onScroll = () => {
      let distance;
      if (isContainerScrollable()) {
        distance = container.scrollHeight - container.scrollTop - container.clientHeight;
      } else {
        // Mobile: die ganze Seite scrollt → window benutzen
        const doc = document.documentElement;
        distance = doc.scrollHeight - window.innerHeight - (window.scrollY || doc.scrollTop || 0);
      }
      setShowScrollDown(distance > 150);
    };
    onScroll();
    container?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Periodischer Re-Check (z.B. wenn StoryLog expand toggelt und Höhe ändert)
    const interval = setInterval(onScroll, 1500);
    return () => {
      container?.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      clearInterval(interval);
    };
  }, [messages.length]);

  const scrollToBottom = () => {
    const container = logRef.current;
    if (container && container.scrollHeight - container.clientHeight > 20) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    } else {
      // Mobile-Fallback: window scrollen
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    }
  };

  const lastGmMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "gm") return messages[i];
    }
    return null;
  }, [messages]);

  const shuffledOptions = useMemo(() => {
    const presets = PRESET_ACTIONS.map((p) => ({
      ...p,
      testId: `action-btn-preset-${p.id.replace("preset-", "")}`,
    }));
    const gmChoices = (lastGmMessage?.choices || []).map((c, i) => ({
      id: `choice-${lastGmMessage.id}-${i}`,
      label: c.text,
      text: c.text,
      withDice: true,
      icon: c.type === "spotlight" ? Star : ArrowRight,
      choiceType: c.type,
      testId: c.type === "spotlight" ? `choice-spotlight-btn-${i}` : `choice-btn-${i}`,
    }));
    const all = [...presets, ...gmChoices];
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    return all;
  }, [lastGmMessage?.id]);

  const remaining = user?.actions_remaining_today ?? 0;
  const limit = user?.daily_limit ?? 10;
  const limitReached = remaining <= 0;

  const handleSend = async (overrideText, _ignored, choiceType = null, source = "free_text") => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    if (limitReached) {
      toast.error("Die Hobby Horses sind heute müde 🐴. Komm morgen wieder.");
      return;
    }
    if (overrideText === undefined) setInput("");
    setSending(true);
    setStreamingGmText("");
    try {
      await streamAction(text, (evt) => {
        if (evt.type === "player") {
          if (evt.data.dice_value !== null && evt.data.dice_value !== undefined) {
            setDiceOverlay({ value: evt.data.dice_value, outcome: evt.data.dice_outcome });
          }
          setMessages((prev) => [...prev, evt.data]);
          // Scroll user's freshly-sent action into view at the TOP
          setScrollTargetId(evt.data.id);
        } else if (evt.type === "token") {
          setStreamingGmText((prev) => prev + evt.data);
        } else if (evt.type === "done") {
          const gmId = evt.data.gm_message?.id;
          setMessages((prev) => [...prev, evt.data.gm_message]);
          setStreamingGmText("");
          setProfile(evt.data.profile);
          if (evt.data.user) setUser(evt.data.user);
          if (evt.data.level_up_available) setLevelUpAvailable(true);
          if (evt.data.achievements_unlocked?.length) {
            for (const a of evt.data.achievements_unlocked) {
              toast.success(`🏆 Auszeichnung: ${a.name}`, { description: a.description, duration: 4500 });
            }
          }
          if (evt.data.items_granted?.length) {
            for (const it of evt.data.items_granted) {
              toast.success(`📦 Neuer Gegenstand: ${it.name}`, {
                description: it.description || "Frisch ins Inventar aufgenommen.",
                duration: 6500,
              });
            }
          }
          if (evt.data.challenge_completed) {
            const cc = evt.data.challenge_completed;
            toast.success(`🏁 Galopp-Challenge erledigt: ${cc.challenge.name}`, {
              description: `Hammerl überweist dir spontan +${cc.reward_xp} XP.`,
              duration: 7000,
            });
          }
          // v14.2: Beziehungs-Meilensteine als Toast (75/90/100)
          if (evt.data.milestones_unlocked?.length) {
            for (const m of evt.data.milestones_unlocked) {
              toast.success(`💫 Meilenstein ${m.threshold} mit ${m.npc} erreicht!`, {
                description: `Sonder-Item: ${m.item_name}`,
                duration: 7500,
              });
            }
          }
          setChallengeKey((k) => k + 1);
          // When GM has finished writing, snap to the START of the new GM message
          if (gmId) setScrollTargetId(gmId);
        } else if (evt.type === "error") {
          throw new Error(evt.data || "Stream-Fehler");
        }
      }, choiceType, source);
    } catch (err) {
      if (err.status === 429) {
        toast.error(err.message);
        refresh();
      } else if (err.networkAborted || err.status === 0) {
        // Mobile Background / Connection-Drop → Server verarbeitet trotzdem, einfach state reloaden
        toast.info("Hammerl verarbeitet deine Aktion im Hintergrund weiter — lade Verlauf nach…", { duration: 4000 });
        try {
          const [p, m] = await Promise.all([getMyProfile(), getMyMessages()]);
          setProfile(p);
          setMessages(m);
          refresh();
        } catch (e) {
          console.error("[GamePage] Reload after network abort failed:", e);
        }
      } else if (err.status === 409) {
        // Pending-Action-Lock: vorherige Aktion noch nicht abgeschlossen → kein neuer Würfel
        toast.warning(err.message || "Bitte einen Moment, deine letzte Aktion läuft noch.");
        try {
          const [p, m] = await Promise.all([getMyProfile(), getMyMessages()]);
          setProfile(p);
          setMessages(m);
        } catch (e) {
          console.error("[GamePage] Reload after 409 lock failed:", e);
        }
      } else {
        toast.error(err?.message || "Der Game Master schweigt — versuch es nochmal.");
      }
      setStreamingGmText("");
    } finally {
      setSending(false);
    }
  };

  const handlePickOption = (opt) => {
    if (sending || !opt || limitReached) return;
    const source = opt.id?.startsWith("preset-") ? "preset" : "choice";
    handleSend(opt.text, null, opt.choiceType || null, source);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success("Account dauerhaft gelöscht. Auf Wiedersehen, Galopp-Talent.");
      logout();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Account konnte nicht gelöscht werden.");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleLevelUp = async (skillId) => {
    try {
      const updated = await levelUp(skillId);
      setProfile(updated);
      setLevelUpOpen(false);
      setLevelUpAvailable(updated.xp >= updated.level * (updated.level + 1) * 50);
      const m = await getMyMessages();
      setMessages(m);
      toast.success(`Level ${updated.level} erreicht! Stempel: BEFÖRDERT`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Level-Up fehlgeschlagen.");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  if (!profile || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hammerl-paper text-hammerl-pencil font-mono text-sm">
        Synergisiere Profil…
      </div>
    );
  }

  const counterColor =
    remaining <= 0 ? "bg-hammerl-fail text-white border-hammerl-ink" :
    remaining <= 3 ? "bg-hammerl-mixed text-hammerl-paper border-hammerl-ink" :
    "bg-hammerl-highlight text-hammerl-paper border-hammerl-ink";

  return (
    <div className="min-h-screen bg-hammerl-paper text-hammerl-ink flex flex-col">
      <header className="border-b-2 border-hammerl-ink bg-hammerl-card">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/brand/hammerl-logo.jpg" alt="Hobby Horsing Hammerl Logo" className="w-9 h-9 object-cover border border-hammerl-ink" />
            <div>
              <div className="font-bold text-xs sm:text-sm tracking-tight">Hobby Horsing Hammerl</div>
              <div className="text-[10px] sm:text-xs text-hammerl-pencil font-mono">Reiter: {profile.name}</div>
            </div>
          </div>

          {/* Daily Counter */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 ${counterColor} font-mono font-bold text-xs sm:text-sm`} data-testid="daily-counter">
            <Moon className="w-3.5 h-3.5" />
            <span>Heute: {limit - remaining} / {limit} Entscheidungen</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/scoreboard")}
              className="hidden sm:inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-hammerl-ink hover:bg-hammerl-highlight hover:text-hammerl-paper hover:text-hammerl-ink px-2 py-1 transition-colors"
              data-testid="open-scoreboard-btn"
            >
              <BarChart3 className="w-4 h-4" /> Charts
            </button>
            {levelUpAvailable && (
              <Button
                onClick={() => setLevelUpOpen(true)}
                className="rounded-none bg-hammerl-fail hover:bg-hammerl-ink text-white font-bold animate-pulse"
                data-testid="open-level-up-btn"
              >
                <Trophy className="w-4 h-4 mr-1" /> LEVEL-UP!
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-hammerl-ink hover:bg-hammerl-highlight hover:text-hammerl-paper hover:text-hammerl-ink px-2 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-hammerl-corpblue"
                  data-testid="settings-menu-btn"
                  aria-label="Einstellungen"
                  title="Einstellungen"
                >
                  <Settings className="w-4 h-4" />
                  <span className="hidden sm:inline">Menü</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-2 border-hammerl-ink shadow-brutal min-w-[220px] bg-hammerl-card">
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">
                  {user?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-hammerl-ink/30" />
                <DropdownMenuItem
                  onSelect={() => navigate("/scoreboard")}
                  className="sm:hidden font-mono text-xs uppercase tracking-wider cursor-pointer text-hammerl-ink focus:bg-hammerl-highlight focus:text-hammerl-paper"
                  data-testid="menu-scoreboard"
                >
                  <BarChart3 className="w-4 h-4 mr-2" /> Charts
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleLogout}
                  className="font-mono text-xs uppercase tracking-wider cursor-pointer text-hammerl-ink focus:bg-hammerl-highlight focus:text-hammerl-paper"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Abmelden
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-hammerl-ink/30" />
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setDeleteOpen(true); }}
                  className="font-mono text-xs uppercase tracking-wider cursor-pointer text-hammerl-fail focus:bg-hammerl-fail focus:text-white"
                  data-testid="menu-delete-account"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Account löschen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex-1 grid lg:grid-cols-[320px_1fr] xl:grid-cols-[340px_1fr_280px] max-w-[1600px] w-full mx-auto">
        <aside className="border-r-2 border-hammerl-ink bg-hammerl-sidebar p-5 lg:max-h-[calc(100vh-58px)] overflow-y-auto space-y-5">
          <WeeklyChallenge refreshKey={challengeKey} />
          <CharacterSheet profile={profile} onChange={setProfile} />
        </aside>

        <main className="flex flex-col lg:max-h-[calc(100vh-58px)] relative">
          {showScrollDown && (
            <button
              type="button"
              onClick={scrollToBottom}
              data-testid="scroll-to-bottom-btn"
              className="fixed bottom-24 right-4 z-40 lg:absolute lg:top-3 lg:left-4 lg:right-auto lg:bottom-auto group inline-flex items-center gap-2 px-3 py-2 lg:py-1.5 bg-hammerl-card border-2 border-hammerl-ink shadow-brutal hover:translate-y-[1px] hover:translate-x-[1px] hover:shadow-none transition-transform font-mono text-[10px] uppercase tracking-widest text-hammerl-ink"
              aria-label="Zu den aktuellen Antwortmöglichkeiten springen"
              title="Zu den aktuellen Antwortmöglichkeiten springen"
            >
              <ArrowDownCircle className="w-4 h-4 text-hammerl-corpblue" />
              <span className="hidden sm:inline">Zu den Antworten</span>
            </button>
          )}
          <div ref={logRef} className="flex-1 overflow-y-auto p-4 sm:p-6">
            <StoryLog
              messages={messages}
              onShareMessage={setShareMessage}
              profile={profile}
              onProfileChange={setProfile}
            />
            {streamingGmText && (
              <div ref={streamingBubbleRef} className="max-w-3xl mx-auto mt-4 bg-hammerl-card border-2 border-hammerl-ink shadow-brutal px-6 sm:px-12 py-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil mb-2 flex items-center gap-2">
                  <span>Game Master</span>
                  <span className="inline-block w-1.5 h-1.5 bg-hammerl-corpblue animate-pulse" />
                  <span className="text-hammerl-corpblue">verfasst Antwort</span>
                </div>
                <div className="font-handwriting text-xl text-hammerl-pencil italic flex items-center gap-2" data-testid="gm-typing-stream">
                  <span>Der Game Master tippt mit synergetischer Eile</span>
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-hammerl-corpblue rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-hammerl-corpblue rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-hammerl-corpblue rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
            {sending && !streamingGmText && (
              <div ref={streamingBubbleRef} className="max-w-3xl mx-auto mt-4 bg-hammerl-card border-2 border-hammerl-ink shadow-brutal px-6 sm:px-12 py-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil mb-2 flex items-center gap-2">
                  <span>Game Master</span>
                  <span className="inline-block w-1.5 h-1.5 bg-hammerl-corpblue animate-pulse" />
                  <span className="text-hammerl-corpblue">denkt nach</span>
                </div>
                <div className="font-handwriting text-xl text-hammerl-pencil italic flex items-center gap-2" data-testid="gm-thinking">
                  <span>Der Game Master tippt mit synergetischer Eile</span>
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-hammerl-corpblue rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-hammerl-corpblue rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-hammerl-corpblue rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}

            {/* Limit-Reached overlay */}
            {limitReached && (
              <div className="max-w-3xl mx-auto mt-6 bg-hammerl-card border-4 border-hammerl-fail p-6 shadow-brutal" data-testid="limit-reached-card">
                <div className="flex items-start gap-3">
                  <Moon className="w-8 h-8 text-hammerl-fail shrink-0" />
                  <div>
                    <div className="font-bold text-xl mb-1">Die Hobby Horses sind heute müde 🐴</div>
                    <p className="font-handwriting text-xl text-hammerl-blue leading-relaxed">
                      Du hast deine {limit} Entscheidungen für heute aufgebraucht. Der Pferdeflüsterer
                      Voigt regeneriert über Nacht die Galopp-Kapazitäten. Komm morgen wieder vorbei —
                      Hammerl plant bereits die nächste Synergie-Session.
                    </p>
                    <div className="font-mono text-xs uppercase tracking-widest text-hammerl-pencil mt-3">
                      Reset um 00:00 UTC · Hammerl Charts bleiben offen
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Floating "GM tippt..." badge when streaming bubble is off-screen */}
          {sending && showLiveBadge && (
            <button
              type="button"
              onClick={() => streamingBubbleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="absolute bottom-[210px] right-4 sm:right-6 z-20 inline-flex items-center gap-2 px-3 py-2 bg-hammerl-corpblue text-white border-2 border-hammerl-ink shadow-brutal-sm font-mono text-xs uppercase tracking-wider hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
              data-testid="gm-live-badge"
              aria-label="Zum Game Master springen"
            >
              <span className="inline-flex gap-0.5">
                <span className="w-1 h-1 bg-hammerl-card rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1 h-1 bg-hammerl-card rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1 h-1 bg-hammerl-card rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
              GM tippt
            </button>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); handleSend(); }}
            className="border-t-2 border-hammerl-ink bg-hammerl-card p-4 sm:p-5"
            data-testid="action-form"
          >
            <ActionButtons options={shuffledOptions} onPick={handlePickOption} disabled={sending || limitReached} hidden={sending} />

            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono uppercase tracking-widest text-hammerl-pencil">
                Spieleraktion // Formular B-7
              </label>
            </div>
            <div className="flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={limitReached ? "Komm morgen wieder, die Pferde müssen schlafen." : "Was tust du? z.B. 'Ich galoppiere mit voller Galopp-Effizienz auf das brennende Hindernis zu...'"}
                className="flex-1 rounded-none border-2 border-hammerl-ink bg-hammerl-card resize-none min-h-[64px] focus-visible:ring-0 focus-visible:border-hammerl-corpblue font-handwriting text-xl text-hammerl-blue"
                disabled={sending || limitReached}
                data-testid="action-input"
              />
              <Button
                type="submit"
                disabled={sending || !input.trim() || limitReached}
                className="rounded-none bg-hammerl-corpblue hover:bg-hammerl-ink text-white font-bold px-5 h-[64px] shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
                data-testid="send-action-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-[10px] font-mono text-hammerl-pencil mt-2">Enter zum Absenden · Shift+Enter für neue Zeile</div>
          </form>
        </main>

        <aside className="hidden xl:block border-l-2 border-hammerl-ink bg-hammerl-card p-5 lg:max-h-[calc(100vh-58px)] overflow-y-auto">
          <div className="font-mono text-xs uppercase tracking-widest text-hammerl-pencil mb-3">Galopp-Tipps</div>
          <div className="space-y-4">
            <div className="border border-hammerl-ink p-3">
              <Sparkles className="w-4 h-4 mb-1 text-hammerl-corpblue" />
              <div className="font-bold text-sm">10 Entscheidungen täglich</div>
              <div className="text-xs text-hammerl-pencil">Reset um 00:00 UTC. Plane deine Galoppstrategie.</div>
            </div>
            <div className="border border-hammerl-ink p-3">
              <Sword className="w-4 h-4 mb-1 text-hammerl-corpblue" />
              <div className="font-bold text-sm">Steckenpferd pflegen</div>
              <div className="text-xs text-hammerl-pencil">Schorschi überwacht das Seelenheil deiner "Standard-Fichte".</div>
            </div>
            <div className="border border-hammerl-ink p-3">
              <Trophy className="w-4 h-4 mb-1 text-hammerl-corpblue" />
              <div className="font-bold text-sm">Level-Aufstieg</div>
              <div className="text-xs text-hammerl-pencil">Ab Level × 100 XP darfst du einen absurden Skill wählen.</div>
            </div>
            <div className="border border-hammerl-ink p-3">
              <Award className="w-4 h-4 mb-1 text-hammerl-corpblue" />
              <div className="font-bold text-sm">Auszeichnungen sammeln</div>
              <div className="text-xs text-hammerl-pencil">Erkunde die Welt — versteckte Achievements warten überall.</div>
            </div>
          </div>
          <div className="mt-6 font-handwriting text-xl text-hammerl-blue -rotate-1">
            "Wer würfelt, gewinnt zumindest Aufmerksamkeit." — Ungerino
          </div>
        </aside>
      </div>

      <AnimatePresence>
        {diceOverlay && (
          <DiceRollOverlay
            value={diceOverlay.value}
            outcome={diceOverlay.outcome}
            onClose={() => setDiceOverlay(null)}
          />
        )}
      </AnimatePresence>

      <LevelUpDialog
        open={levelUpOpen}
        onOpenChange={setLevelUpOpen}
        onConfirm={handleLevelUp}
        level={profile.level + 1}
      />

      <ShareCardDialog
        open={Boolean(shareMessage)}
        onOpenChange={(v) => { if (!v) setShareMessage(null); }}
        message={shareMessage}
        profile={profile}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded-none border-2 border-hammerl-ink shadow-brutal bg-hammerl-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-mono uppercase tracking-wider text-hammerl-fail flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Account dauerhaft löschen?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-handwriting text-lg text-hammerl-blue leading-relaxed">
              Hammerl gibt zu bedenken: <span className="font-bold">alle</span> deine Daten — Profil,
              Level, XP, Inventar, Auszeichnungen und der komplette Galopp-Verlauf —
              werden DSGVO-konform und unwiderruflich aus den Synergie-Systemen entfernt. Diese Aktion
              kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-none border-2 border-hammerl-ink bg-hammerl-card hover:bg-hammerl-highlight hover:text-hammerl-paper font-mono text-xs uppercase tracking-wider text-hammerl-ink"
              data-testid="delete-account-cancel"
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="rounded-none border-2 border-hammerl-ink bg-hammerl-fail hover:bg-hammerl-ink text-white font-mono text-xs uppercase tracking-wider"
              data-testid="delete-account-confirm"
            >
              {deleting ? "Lösche…" : "Endgültig löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
